import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Building2,
  ArrowLeft,
  Loader2,
  CreditCard,
  FileText,
  Settings,
  Users,
  Shield,
  ShieldOff,
  Plus,
  Edit,
  Save,
  Check,
  Package,
  ToggleLeft,
  ToggleRight,
  Infinity
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { getUserErrorMessage } from '../../utils/errorMessages';
import { Switch } from '../../components/ui/switch';

const PLANS = [
  { id: 'starter', name: 'Starter', price: '€0/maand' },
  { id: 'professional', name: 'Professional', price: '€99/maand' },
  { id: 'enterprise', name: 'Enterprise', price: '€299/maand' }
];

const AVAILABLE_MODULES = [
  { id: 'assessments', name: 'Assessments', description: 'Security assessments en vragenlijsten' },
  { id: 'vulnerabilities', name: 'Kwetsbaarheden', description: 'Kwetsbaarheden identificatie en beheer' },
  { id: 'risks', name: 'Risicobeheer', description: 'Risico analyse en mitigatie' },
  { id: 'measures', name: 'Maatregelen', description: 'Security maatregelen en controls' },
  { id: 'suppliers', name: 'Leveranciers', description: 'Leveranciersbeheer en marketplace' },
  { id: 'compliance', name: 'Compliance', description: 'Framework compliance tracking' },
  { id: 'reporting', name: 'Rapportages', description: 'Geavanceerde rapportages en exports' },
  { id: 'api_access', name: 'API Toegang', description: 'REST API integratie mogelijkheden' }
];

const AdminOrganizationDetail = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  const [limitedAccess, setLimitedAccess] = useState(false);
  
  const [organization, setOrganization] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orgModules, setOrgModules] = useState([]);
  const [assessmentLimits, setAssessmentLimits] = useState(null);
  
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    amount: '',
    description: '',
    status: 'pending'
  });

  useEffect(() => {
    fetchData();
  }, [orgId]);

  const fetchData = async () => {
    try {
      // Fetch org detail — may return 403 if admin access disabled
      const orgRes = await api.get(`/admin/organizations/${orgId}`).catch(() => ({ data: null }));
      
      if (orgRes.data) {
        setOrganization(orgRes.data);
        const hasFullAccess = orgRes.data?.admin_access_enabled === true;
        setLimitedAccess(!hasFullAccess);
        
        if (hasFullAccess) {
          const [subRes, invRes] = await Promise.all([
            api.get(`/admin/organizations/${orgId}/subscription`).catch(() => ({ data: null })),
            api.get(`/admin/organizations/${orgId}/invoices`).catch(() => ({ data: [] }))
          ]);
          setSubscription(subRes.data);
          setInvoices(invRes.data || []);
        }
      } else {
        // Org detail blocked — fetch basic info from org list
        const listRes = await api.get('/admin/organizations').catch(() => ({ data: [] }));
        const orgFromList = (listRes.data || []).find(o => o.id === orgId);
        if (orgFromList) setOrganization(orgFromList);
        setLimitedAccess(true);
      }
      
      // Always fetch these independently — not blocked by admin access
      const [settingsRes, modulesRes, limitsRes] = await Promise.all([
        api.get(`/admin/organizations/${orgId}/settings`).catch(() => ({ data: null })),
        api.get(`/admin/organizations/${orgId}/modules`).catch(() => ({ data: [] })),
        api.get(`/admin/organizations/${orgId}/assessment-limits`).catch(() => ({ data: null }))
      ]);
      
      setSettings(settingsRes.data);
      setOrgModules(modulesRes.data || []);
      if (limitsRes.data) setAssessmentLimits(limitsRes.data);
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Data laden mislukt'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganization = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${orgId}`, organization);
      toast.success('Organisatie bijgewerkt');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSubscription = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${orgId}/subscription`, subscription);
      toast.success('Abonnement bijgewerkt');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.amount || !invoiceForm.description) {
      toast.error('Vul alle velden in');
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post(`/admin/organizations/${orgId}/invoices`, {
        amount: parseFloat(invoiceForm.amount),
        description: invoiceForm.description,
        status: invoiceForm.status
      });
      setInvoices([res.data, ...invoices]);
      setShowInvoiceModal(false);
      setInvoiceForm({ amount: '', description: '', status: 'pending' });
      toast.success('Factuur aangemaakt');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Aanmaken mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAssessmentLimits = async () => {
    if (!assessmentLimits) return;
    setSaving(true);
    try {
      await api.patch(`/admin/organizations/${orgId}/assessment-limits`, assessmentLimits);
      toast.success('Nulmeting limieten opgeslagen');
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'd MMM yyyy', { locale: nl });
    } catch {
      return dateString;
    }
  };

  const handleToggleModule = async (moduleId) => {
    const isEnabled = orgModules.includes(moduleId);
    const newModules = isEnabled 
      ? orgModules.filter(m => m !== moduleId)
      : [...orgModules, moduleId];
    
    try {
      await api.patch(`/admin/organizations/${orgId}/modules`, { modules: newModules });
      setOrgModules(newModules);
      toast.success(isEnabled ? 'Module uitgeschakeld' : 'Module ingeschakeld');
    } catch (error) {
      toast.error('Module update mislukt');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/organizations')}
            >
              <ArrowLeft className="h-5 w-5" strokeWidth={1.5} />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight text-black">
                  {organization?.name}
                </h1>
                {limitedAccess ? (
                  <Badge className="bg-amber-100 text-amber-700">
                    <ShieldOff className="h-3 w-3 mr-1" />
                    Beperkte toegang
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700">
                    <Shield className="h-3 w-3 mr-1" />
                    Volledige toegang
                  </Badge>
                )}
              </div>
              <p className="text-sm text-neutral-500 mt-1">
                {limitedAccess 
                  ? 'Beperkte weergave - organisatie heeft admin toegang niet ingeschakeld'
                  : 'Beheer klantgegevens voor deze organisatie'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-neutral-100 p-1">
            <TabsTrigger 
              value="organization" 
              className="data-[state=active]:bg-white"
              data-testid="tab-organization"
            >
              <Building2 className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Organisatie
            </TabsTrigger>
            <TabsTrigger 
              value="users" 
              className="data-[state=active]:bg-white"
              data-testid="tab-users"
            >
              <Users className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Gebruikers
            </TabsTrigger>
            <TabsTrigger 
              value="modules" 
              className="data-[state=active]:bg-white"
              data-testid="tab-modules"
            >
              <Package className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Modules
            </TabsTrigger>
            {!limitedAccess && (
              <>
                <TabsTrigger 
                  value="subscription" 
                  className="data-[state=active]:bg-white"
                  data-testid="tab-subscription"
                >
                  <CreditCard className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Abonnement
                </TabsTrigger>
                <TabsTrigger 
                  value="invoices" 
                  className="data-[state=active]:bg-white"
                  data-testid="tab-invoices"
                >
                  <FileText className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Facturen
                </TabsTrigger>
                <TabsTrigger 
                  value="settings" 
                  className="data-[state=active]:bg-white"
                  data-testid="tab-settings"
                >
                  <Settings className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Instellingen
                </TabsTrigger>
              </>
            )}
            <TabsTrigger 
              value="nulmeting" 
              className="flex items-center gap-1.5"
              data-testid="tab-nulmeting"
            >
              <Shield className="h-3.5 w-3.5" />
              Nulmeting Limieten
            </TabsTrigger>
          </TabsList>

          {/* Organization Tab */}
          <TabsContent value="organization" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle>Organisatie Gegevens</CardTitle>
                <CardDescription>Beheer de basisgegevens van deze organisatie</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Bedrijfsnaam</Label>
                    <Input
                      value={organization?.name || ''}
                      onChange={(e) => setOrganization({ ...organization, name: e.target.value })}
                      data-testid="org-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>KVK Nummer</Label>
                    <Input
                      value={organization?.kvk_number || ''}
                      onChange={(e) => setOrganization({ ...organization, kvk_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Adres</Label>
                    <Input
                      value={organization?.address || ''}
                      onChange={(e) => setOrganization({ ...organization, address: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input
                      value={organization?.postal_code || ''}
                      onChange={(e) => setOrganization({ ...organization, postal_code: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stad</Label>
                    <Input
                      value={organization?.city || ''}
                      onChange={(e) => setOrganization({ ...organization, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Land</Label>
                    <Input
                      value={organization?.country || ''}
                      onChange={(e) => setOrganization({ ...organization, country: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telefoon</Label>
                    <Input
                      value={organization?.phone || ''}
                      onChange={(e) => setOrganization({ ...organization, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input
                      value={organization?.website || ''}
                      onChange={(e) => setOrganization({ ...organization, website: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button
                    className="bg-black text-white hover:bg-neutral-800"
                    onClick={handleSaveOrganization}
                    disabled={saving}
                    data-testid="save-org-button"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Opslaan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab - Always visible */}
          <TabsContent value="users" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" strokeWidth={1.5} />
                  Gebruikers ({settings?.user_count || 0})
                </CardTitle>
                <CardDescription>Overzicht van alle gebruikers in deze organisatie</CardDescription>
              </CardHeader>
              <CardContent>
                {settings?.users?.length > 0 ? (
                  <div className="space-y-2">
                    {settings.users.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-4 bg-neutral-50 rounded-lg border border-neutral-100">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-neutral-200 flex items-center justify-center">
                            <span className="text-sm font-medium">{u.full_name?.charAt(0) || 'U'}</span>
                          </div>
                          <div>
                            <p className="font-medium">{u.full_name}</p>
                            <p className="text-sm text-neutral-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">{u.role}</Badge>
                          {u.mfa_enabled && (
                            <Badge className="bg-emerald-100 text-emerald-700">MFA</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-sm text-neutral-500">Geen gebruikers gevonden</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules Tab - Always visible */}
          <TabsContent value="modules" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" strokeWidth={1.5} />
                  Modules
                </CardTitle>
                <CardDescription>Beheer welke modules beschikbaar zijn voor deze organisatie</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {AVAILABLE_MODULES.map((module) => {
                    const isEnabled = orgModules.includes(module.id);
                    return (
                      <div 
                        key={module.id}
                        className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                          isEnabled 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-neutral-200 bg-neutral-50 hover:border-neutral-300'
                        }`}
                        onClick={() => handleToggleModule(module.id)}
                        data-testid={`module-${module.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-black">{module.name}</h4>
                            <p className="text-sm text-neutral-500 mt-1">{module.description}</p>
                          </div>
                          <div className="ml-4">
                            {isEnabled ? (
                              <ToggleRight className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
                            ) : (
                              <ToggleLeft className="h-6 w-6 text-neutral-400" strokeWidth={1.5} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 p-4 bg-neutral-100 rounded-lg">
                  <p className="text-sm text-neutral-600">
                    <strong>Actieve modules:</strong> {orgModules.length} van {AVAILABLE_MODULES.length}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab - Only with full access */}
          {!limitedAccess && (
            <>
              <TabsContent value="subscription" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle>Abonnement</CardTitle>
                <CardDescription>Beheer het abonnement van deze organisatie</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Abonnement Type</Label>
                    <Select
                      value={subscription?.plan_id || 'starter'}
                      onValueChange={(v) => setSubscription({ ...subscription, plan_id: v })}
                    >
                      <SelectTrigger data-testid="plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PLANS.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name} - {plan.price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={subscription?.status || 'active'}
                      onValueChange={(v) => setSubscription({ ...subscription, status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Actief</SelectItem>
                        <SelectItem value="trial">Proefperiode</SelectItem>
                        <SelectItem value="cancelled">Opgezegd</SelectItem>
                        <SelectItem value="suspended">Opgeschort</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Factureringsperiode</Label>
                    <Select
                      value={subscription?.billing_cycle || 'monthly'}
                      onValueChange={(v) => setSubscription({ ...subscription, billing_cycle: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Maandelijks</SelectItem>
                        <SelectItem value="yearly">Jaarlijks</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Volgende Factuurdatum</Label>
                    <Input
                      type="date"
                      value={subscription?.next_billing_date?.split('T')[0] || ''}
                      onChange={(e) => setSubscription({ ...subscription, next_billing_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="flex justify-end pt-4">
                  <Button
                    className="bg-black text-white hover:bg-neutral-800"
                    onClick={handleSaveSubscription}
                    disabled={saving}
                    data-testid="save-sub-button"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Opslaan
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab - Only with full access */}
              <TabsContent value="invoices" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Facturen</CardTitle>
                  <CardDescription>Overzicht en beheer van facturen</CardDescription>
                </div>
                <Button
                  className="bg-black text-white hover:bg-neutral-800"
                  onClick={() => setShowInvoiceModal(true)}
                  data-testid="create-invoice-button"
                >
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Nieuwe Factuur
                </Button>
              </CardHeader>
              <CardContent>
                {invoices.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th className="text-left text-xs font-medium text-neutral-500 p-3">Factuurnummer</th>
                          <th className="text-left text-xs font-medium text-neutral-500 p-3">Beschrijving</th>
                          <th className="text-left text-xs font-medium text-neutral-500 p-3">Bedrag</th>
                          <th className="text-left text-xs font-medium text-neutral-500 p-3">Status</th>
                          <th className="text-left text-xs font-medium text-neutral-500 p-3">Datum</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-neutral-50">
                            <td className="p-3 text-sm font-medium">{invoice.invoice_number}</td>
                            <td className="p-3 text-sm text-neutral-600">{invoice.description}</td>
                            <td className="p-3 text-sm">€{invoice.amount?.toFixed(2)}</td>
                            <td className="p-3">
                              <Badge className={
                                invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                invoice.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                'bg-red-100 text-red-700'
                              }>
                                {invoice.status === 'paid' ? 'Betaald' :
                                 invoice.status === 'pending' ? 'Open' : 'Vervallen'}
                              </Badge>
                            </td>
                            <td className="p-3 text-sm text-neutral-600">{formatDate(invoice.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-10 w-10 text-neutral-300 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-sm text-neutral-500">Geen facturen gevonden</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab - Only with full access */}
              <TabsContent value="settings" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle>Organisatie Instellingen</CardTitle>
                <CardDescription>Geavanceerde instellingen voor deze organisatie</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-neutral-50 rounded-lg">
                    <p className="text-sm text-neutral-500">Admin Toegang</p>
                    <p className="font-medium">{organization?.admin_access_enabled ? 'Ingeschakeld' : 'Uitgeschakeld'}</p>
                  </div>
                  <div className="p-4 bg-neutral-50 rounded-lg">
                    <p className="text-sm text-neutral-500">Framework</p>
                    <p className="font-medium">{organization?.framework_id || 'Geen'}</p>
                  </div>
                  <div className="p-4 bg-neutral-50 rounded-lg">
                    <p className="text-sm text-neutral-500">Aangemaakt</p>
                    <p className="font-medium">{formatDate(organization?.created_at)}</p>
                  </div>
                  <div className="p-4 bg-neutral-50 rounded-lg">
                    <p className="text-sm text-neutral-500">Laatste Update</p>
                    <p className="font-medium">{formatDate(organization?.updated_at)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
              </TabsContent>
            </>
          )}

          {/* Nulmeting Limieten Tab */}
          <TabsContent value="nulmeting" className="mt-6">
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle>Nulmeting Domein Limieten</CardTitle>
                <CardDescription>Stel per organisatie de maximale aantallen in voor elk nulmeting-domein</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {assessmentLimits ? (
                  <>
                    <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-2">
                      <p className="text-xs text-blue-700">Waarde <strong>0</strong> = onbeperkt. Gebruik de schakelaar om een limiet in of uit te schakelen.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Mensen */}
                      <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 space-y-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Mensen</p>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-neutral-600">Max campagnes</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.people_max_campaigns !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, people_max_campaigns: checked ? 3 : 0})} data-testid="toggle-people-campaigns" />
                              {assessmentLimits.people_max_campaigns !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.people_max_campaigns} onChange={e => setAssessmentLimits({...assessmentLimits, people_max_campaigns: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-people-campaigns" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-neutral-600">Max vragenlijsten</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.people_max_questionnaires !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, people_max_questionnaires: checked ? 5 : 0})} data-testid="toggle-people-questionnaires" />
                              {assessmentLimits.people_max_questionnaires !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.people_max_questionnaires} onChange={e => setAssessmentLimits({...assessmentLimits, people_max_questionnaires: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-people-questionnaires" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Processen */}
                      <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 space-y-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Processen</p>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-neutral-600">Max frameworks</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.process_max_frameworks !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, process_max_frameworks: checked ? 4 : 0})} data-testid="toggle-process-frameworks" />
                              {assessmentLimits.process_max_frameworks !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.process_max_frameworks} onChange={e => setAssessmentLimits({...assessmentLimits, process_max_frameworks: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-process-frameworks" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-neutral-600">Max vragenlijsten</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.process_max_questionnaires !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, process_max_questionnaires: checked ? 10 : 0})} data-testid="toggle-process-questionnaires" />
                              {assessmentLimits.process_max_questionnaires !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.process_max_questionnaires} onChange={e => setAssessmentLimits({...assessmentLimits, process_max_questionnaires: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-process-questionnaires" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Technologie */}
                      <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200 space-y-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Technologie</p>
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs text-neutral-600">Max scans</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.technology_max_scans !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, technology_max_scans: checked ? 5 : 0})} data-testid="toggle-tech-scans" />
                              {assessmentLimits.technology_max_scans !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.technology_max_scans} onChange={e => setAssessmentLimits({...assessmentLimits, technology_max_scans: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-tech-scans" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-neutral-600">Max targets</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Switch checked={assessmentLimits.technology_max_targets !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, technology_max_targets: checked ? 20 : 0})} data-testid="toggle-tech-targets" />
                              {assessmentLimits.technology_max_targets !== 0 ? (
                                <Input type="number" min={1} value={assessmentLimits.technology_max_targets} onChange={e => setAssessmentLimits({...assessmentLimits, technology_max_targets: parseInt(e.target.value) || 1})} className="h-8 text-sm flex-1" data-testid="limit-tech-targets" />
                              ) : (
                                <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                      <Label className="text-xs text-neutral-600">Max re-assessments per jaar</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Switch checked={assessmentLimits.max_reassessments_per_year !== 0} onCheckedChange={(checked) => setAssessmentLimits({...assessmentLimits, max_reassessments_per_year: checked ? 4 : 0})} data-testid="toggle-reassessments" />
                        {assessmentLimits.max_reassessments_per_year !== 0 ? (
                          <Input type="number" min={1} value={assessmentLimits.max_reassessments_per_year} onChange={e => setAssessmentLimits({...assessmentLimits, max_reassessments_per_year: parseInt(e.target.value) || 1})} className="h-8 text-sm w-32" data-testid="limit-reassessments" />
                        ) : (
                          <span className="text-xs font-medium text-blue-600 flex items-center gap-1"><Infinity className="h-3.5 w-3.5" /> Onbeperkt</span>
                        )}
                      </div>
                    </div>
                    <Button onClick={handleSaveAssessmentLimits} disabled={saving} className="bg-black text-white hover:bg-neutral-800" data-testid="save-assessment-limits">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Limieten opslaan
                    </Button>
                  </>
                ) : (
                  <p className="text-sm text-neutral-500">Laden...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nieuwe Factuur</DialogTitle>
            <DialogDescription>Maak een nieuwe factuur aan voor deze organisatie</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Bedrag (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={invoiceForm.amount}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="invoice-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Beschrijving</Label>
              <Input
                value={invoiceForm.description}
                onChange={(e) => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                placeholder="bijv. Maandelijks abonnement - Professional"
                data-testid="invoice-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={invoiceForm.status}
                onValueChange={(v) => setInvoiceForm({ ...invoiceForm, status: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Open</SelectItem>
                  <SelectItem value="paid">Betaald</SelectItem>
                  <SelectItem value="overdue">Vervallen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>
              Annuleren
            </Button>
            <Button
              className="bg-black text-white hover:bg-neutral-800"
              onClick={handleCreateInvoice}
              disabled={saving}
              data-testid="save-invoice-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminOrganizationDetail;
